import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Result,
  Row,
  Space,
  Spin,
  Statistic,
  Tabs,
  Typography,
  Upload,
  message,
} from "antd";
import {
  CheckCircleTwoTone,
  CloudUploadOutlined,
  InboxOutlined,
  PlusOutlined,
  ReloadOutlined,
  TableOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import jspreadsheet from "jspreadsheet-ce";
import * as XLSX from "xlsx";
import { AppContext } from "../context/AppContext";

const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;
const { TabPane } = Tabs;

const ETL_API_URL =
  process.env.REACT_APP_ETL_API_URL ||
  "https://zedlenders.pickmesms.com/jutem-upload";

const DIRECTUS_URL = "https://zedlenders.pickmesms.com";
const JUTEM_COMPANY_ID = 22;

// The stable Jutem monthly loans sheet layout the ETL expects.
const LOAN_COLUMNS = [
  "NAME",
  "AMOUNT LOANED",
  "INTEREST",
  "EXPECTED AMOUNT",
  "DATE EXPECTED",
  "PROFIT",
  "STATUS",
  "COMMISSION",
];
// Back-compat alias (the loans block header).
const MONTH_COLUMNS = LOAN_COLUMNS;

// Sub-header rows for the other stacked sections, matching jutem.xlsx exactly.
const INTEREST_EXPENSE_COLUMNS = [
  "NAME",
  "DATE EXPECTED",
  "BORROWED",
  "INTEREST RATE",
  "PAID",
  "INTEREST EXPENSE",
  "STATUS",
];
const TRACKING_FEES_COLUMNS = ["NAME", "DATE", "FEE"];
const COMMISSION_COLUMNS = ["NAME", "DATE EXPECTED", "COMMISSION"];
const OTHER_EXPENSES_COLUMNS = ["DATE", "EXPENSE", "AMOUNT"];

// Section indices into the jspreadsheet worksheet array (also the tab order).
const SECTION = {
  LOANS: 0,
  INTEREST_EXPENSE: 1,
  TRACKING_FEES: 2,
  COMMISSION: 3,
  OTHER_EXPENSES: 4,
};

// First-column marker text the backend ETL keys on to detect each section.
const MARKER = {
  INTEREST_EXPENSE: "INTEREST EXPENSE",
  TRACKING_FEES: "TRACKING FEES",
  COMMISSION: "COMMISSION",
  OTHER_EXPENSES: "OTHER EXPENSES",
  NET_INTEREST_INCOME: "NET INTEREST INCOME",
  NET_INTEREST_AND_TRACKING: "NET INTEREST INCOME AND TRACKING FEE",
  NET_OPERATING_INCOME: "NET OPERATING INCOME",
  PROFIT_BEFORE_TAX: "PROFIT BEFORE TAX",
  INTEREST_INCOME: "INTEREST INCOME",
  INTEREST_INCOME_RECEIVED: "INTEREST INCOME RECEIVED",
  TRACKING_FEE: "TRACKING FEE",
  TOTAL: "TOTAL",
};

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// Coerce a spreadsheet cell (may contain commas / strings) to a number.
const num = (x) => parseFloat(String(x ?? "").replace(/,/g, "")) || 0;

// Currency formatter for the read-only summary preview (mirrors jutemIncomeStatement).
const fmtK = (val) => {
  const n = parseFloat(val || 0);
  if (n < 0) {
    return `(K ${Math.abs(n).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })})`;
  }
  return `K ${n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// Parse an interest-expense transaction's structured notes field.
// Format: "Interest payment to {name} | Borrowed: K{amt} | Rate: {r}% | Interest: K{amt}"
const parseInterestNotes = (notes) => {
  if (!notes) return { investorName: "", borrowed: 0, rate: 0, interest: 0 };
  const nameMatch = notes.match(/Interest payment to (.+?) \|/);
  const borrowedMatch = notes.match(/Borrowed: K([\d,.]+)/);
  const rateMatch = notes.match(/Rate: ([\d.]+)%/);
  const interestMatch = notes.match(/Interest: K([\d,.]+)/);
  return {
    investorName: nameMatch ? nameMatch[1].trim() : "",
    borrowed: borrowedMatch ? parseFloat(borrowedMatch[1].replace(/,/g, "")) : 0,
    rate: rateMatch ? parseFloat(rateMatch[1]) / 100 : 0,
    interest: interestMatch
      ? parseFloat(interestMatch[1].replace(/,/g, ""))
      : 0,
  };
};

// Sheet name the ETL keys on, e.g. "APRIL 2026" (uppercase, matches /20\d{2}/).
const sheetNameForMonth = (m) => m.format("MMMM YYYY").toUpperCase();

// The sheet's DATE EXPECTED column is a day-of-month ordinal like "25th".
const toDayOrdinal = (isoDate) => {
  if (!isoDate) return "";
  const d = moment(isoDate);
  if (!d.isValid()) return "";
  const day = d.date();
  const suffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
        ? "nd"
        : day % 10 === 3 && day !== 13
          ? "rd"
          : "th";
  return `${day}${suffix}`;
};

const EMPTY_GRID_ROWS = 8;
const blankRowsFor = (colCount, n) =>
  Array.from({ length: n }, () => Array.from({ length: colCount }, () => ""));
const sectionBlankRows = (idx) => {
  switch (idx) {
    case SECTION.LOANS:
    case SECTION.INTEREST_EXPENSE:
    case SECTION.OTHER_EXPENSES:
      return EMPTY_GRID_ROWS;
    default:
      return 4;
  }
};

const formatMoney = (n) =>
  `K ${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const extractSummary = (logText) => {
  const match = logText.match(/\[SUMMARY\]\s*([\s\S]*?)\s*\[\/SUMMARY\]/);
  if (!match) return null;
  try {
    return JSON.parse(match[1]);
  } catch {
    return null;
  }
};

const pickLatestMonthSheet = (sheets) => {
  const monthSheets = (sheets || []).filter((s) => /\b(20\d{2})\b/.test(s));
  return monthSheets.length ? monthSheets[monthSheets.length - 1] : null;
};

const JutemEtlUploadPage = () => {
  const { company } = useContext(AppContext);
  const navigate = useNavigate();

  // phase: 'idle' | 'validating' | 'reviewing' | 'importing' | 'done' | 'error'
  const [phase, setPhase] = useState("idle");
  const [file, setFile] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [summary, setSummary] = useState(null);
  const [log, setLog] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Grid tab state
  const [activeTab, setActiveTab] = useState("upload");
  const [gridMonth, setGridMonth] = useState(moment());
  const [gridLoading, setGridLoading] = useState(false);
  const [, setGridVersion] = useState(0);
  const gridContainerRef = useRef(null);
  const gridInstanceRef = useRef(null);

  useEffect(() => {
    if (company && company.id !== JUTEM_COMPANY_ID) {
      message.warning("This page is only for Jutem Fund (Company ID 22)");
      navigate("/");
    }
  }, [company, navigate]);

  // Initialize the jspreadsheet grid when the grid tab is shown at the idle
  // phase. Written to be resilient to React 19 StrictMode's mount→unmount→
  // remount cycle: we always tear down any prior instance and clear the
  // container before creating a fresh one, and surface (not swallow) errors.
  useEffect(() => {
    if (activeTab !== "grid" || phase !== "idle") return;
    const container = gridContainerRef.current;
    if (!container) return;

    // Destroy any lingering instance from a previous (StrictMode) mount and
    // hard-reset the container so jspreadsheet starts from a clean node.
    try {
      jspreadsheet.destroy(container);
    } catch {
      /* nothing to destroy */
    }
    container.innerHTML = "";

    const sectionOpts = {
      minDimensions: undefined, // set per worksheet below
      allowInsertRow: true,
      allowManualInsertRow: true,
      allowInsertColumn: false,
      allowDeleteColumn: false,
    };

    try {
      const instance = jspreadsheet(container, {
        tabs: true,
        onafterchanges: () => setGridVersion((v) => v + 1),
        worksheets: [
          {
            worksheetName: "Loans",
            data: blankRowsFor(MONTH_COLUMNS.length, EMPTY_GRID_ROWS),
            columns: [
              { type: "text", title: "NAME", width: 200 },
              { type: "numeric", title: "AMOUNT LOANED", width: 130 },
              { type: "numeric", title: "INTEREST", width: 100 },
              { type: "numeric", title: "EXPECTED AMOUNT", width: 140 },
              { type: "text", title: "DATE EXPECTED", width: 120 },
              { type: "numeric", title: "PROFIT", width: 120 },
              {
                type: "dropdown",
                title: "STATUS",
                width: 110,
                source: ["Paid", "Unpaid"],
              },
              { type: "numeric", title: "COMMISSION", width: 120 },
            ],
            minDimensions: [MONTH_COLUMNS.length, EMPTY_GRID_ROWS],
            ...sectionOpts,
          },
          {
            worksheetName: "Interest Expense",
            data: blankRowsFor(INTEREST_EXPENSE_COLUMNS.length, EMPTY_GRID_ROWS),
            columns: [
              { type: "text", title: "NAME", width: 200 },
              { type: "text", title: "DATE EXPECTED", width: 120 },
              { type: "numeric", title: "BORROWED", width: 130 },
              { type: "numeric", title: "INTEREST RATE", width: 120 },
              { type: "numeric", title: "PAID", width: 130 },
              { type: "numeric", title: "INTEREST EXPENSE", width: 140 },
              {
                type: "dropdown",
                title: "STATUS",
                width: 110,
                source: ["Paid", "Unpaid", ""],
              },
            ],
            minDimensions: [INTEREST_EXPENSE_COLUMNS.length, EMPTY_GRID_ROWS],
            ...sectionOpts,
          },
          {
            worksheetName: "Tracking Fees",
            data: blankRowsFor(TRACKING_FEES_COLUMNS.length, 4),
            columns: [
              { type: "text", title: "NAME", width: 220 },
              { type: "text", title: "DATE", width: 140 },
              { type: "numeric", title: "FEE", width: 140 },
            ],
            minDimensions: [TRACKING_FEES_COLUMNS.length, 4],
            ...sectionOpts,
          },
          {
            worksheetName: "Commission",
            data: blankRowsFor(COMMISSION_COLUMNS.length, 4),
            columns: [
              { type: "text", title: "NAME", width: 220 },
              { type: "text", title: "DATE EXPECTED", width: 140 },
              { type: "numeric", title: "COMMISSION", width: 140 },
            ],
            minDimensions: [COMMISSION_COLUMNS.length, 4],
            ...sectionOpts,
          },
          {
            worksheetName: "Other Expenses",
            data: blankRowsFor(OTHER_EXPENSES_COLUMNS.length, EMPTY_GRID_ROWS),
            columns: [
              { type: "text", title: "DATE", width: 140 },
              { type: "text", title: "EXPENSE", width: 260 },
              { type: "numeric", title: "AMOUNT", width: 140 },
            ],
            minDimensions: [OTHER_EXPENSES_COLUMNS.length, EMPTY_GRID_ROWS],
            ...sectionOpts,
          },
        ],
      });
      // v5 returns an array of worksheet instances — keep the whole array so we
      // can read/write each section by its SECTION index.
      gridInstanceRef.current = Array.isArray(instance) ? instance : [instance];
      // Pre-fill the current month now that the grid is ready.
      loadMonthIntoGrid(gridMonth);
    } catch (err) {
      gridInstanceRef.current = null;
      message.error(`Couldn't start the grid editor: ${err.message}`);
    }

    return () => {
      try {
        jspreadsheet.destroy(container);
      } catch {
        /* already destroyed */
      }
      gridInstanceRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, phase]);

  // Set one section worksheet's data, falling back to blank rows when empty.
  const setSectionData = (idx, colCount, data) => {
    const inst = gridInstanceRef.current;
    if (!inst || !inst[idx]) return;
    inst[idx].setData(
      data.length ? data : blankRowsFor(colCount, sectionBlankRows(idx)),
    );
    setGridVersion((v) => v + 1);
  };

  // Pre-fill the Loans section from Directus amortization for the month.
  const loadLoans = async (monthKey, headers) => {
    const res = await axios.get(`${DIRECTUS_URL}/items/amortization`, {
      headers,
      params: {
        "filter[loan_reference][company][_eq]": JUTEM_COMPANY_ID,
        fields:
          "*,loan_reference.amount,loan_reference.borrower.first_name,loan_reference.borrower.last_name",
        limit: -1,
        sort: "-due_date",
      },
    });
    const rows = (res.data.data || []).filter((r) =>
      (r.due_date || "").startsWith(monthKey),
    );
    const data = rows.map((r) => {
      const b = r.loan_reference?.borrower;
      const name = b ? `${b.first_name || ""} ${b.last_name || ""}`.trim() : "";
      const statusLower = (r.status || "").toLowerCase();
      const status = ["paid", "cleared"].includes(statusLower)
        ? "Paid"
        : "Unpaid";
      return [
        name,
        r.loan_reference?.amount ?? "",
        r.interest_rate ?? "",
        r.expected_amount ?? r.amount_due ?? "",
        toDayOrdinal(r.due_date),
        r.profit ?? "",
        status,
        r.commission ?? "",
      ];
    });
    setSectionData(SECTION.LOANS, MONTH_COLUMNS.length, data);
    return data.length;
  };

  // Pre-fill the Interest Expense section from transactions for the month.
  const loadInterestExpense = async (monthKey, headers) => {
    const res = await axios.get(`${DIRECTUS_URL}/items/transactions`, {
      headers,
      params: {
        filter: {
          company: { _eq: JUTEM_COMPANY_ID },
          transaction_type: { _eq: "interest_expense" },
        },
        fields: "id,notes,amount,transaction_date",
        limit: -1,
        sort: "transaction_date",
      },
    });
    const rows = (res.data.data || [])
      .filter((t) => (t.transaction_date || "").startsWith(monthKey))
      .map((t) => {
        const p = parseInterestNotes(t.notes);
        return [
          p.investorName,
          t.transaction_date || "",
          p.borrowed || "",
          p.rate || "",
          p.borrowed + p.interest || "",
          num(t.amount) || "",
          "Paid",
        ];
      });
    setSectionData(SECTION.INTEREST_EXPENSE, INTEREST_EXPENSE_COLUMNS.length, rows);
  };

  // Pre-fill the Other Expenses section from "Operational Cost" transactions.
  const loadOtherExpenses = async (monthKey, headers) => {
    const res = await axios.get(`${DIRECTUS_URL}/items/transactions`, {
      headers,
      params: {
        "filter[company][_eq]": JUTEM_COMPANY_ID,
        "filter[transaction_type][_eq]": "Operational Cost",
        fields: "amount,notes,transaction_date",
        limit: -1,
        sort: "transaction_date",
      },
    });
    const rows = (res.data.data || [])
      .filter((t) => (t.transaction_date || "").startsWith(monthKey))
      .map((t) => [
        t.transaction_date || "",
        t.notes || "",
        num(t.amount) || "",
      ]);
    setSectionData(SECTION.OTHER_EXPENSES, OTHER_EXPENSES_COLUMNS.length, rows);
  };

  // Load every section for the chosen month. Each fetch is isolated so one
  // failing section doesn't blank the others.
  const loadMonthIntoGrid = async (m) => {
    const inst = gridInstanceRef.current;
    if (!inst || !inst.length) return;
    const monthKey = m.format("YYYY-MM");
    const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    setGridLoading(true);

    let loanCount = 0;
    try {
      loanCount = await loadLoans(monthKey, headers);
    } catch (err) {
      setSectionData(SECTION.LOANS, MONTH_COLUMNS.length, []);
      if (!(err.response?.status === 403 || err.response?.status === 404)) {
        message.error("Couldn't load loans for this month");
      }
    }

    try {
      await loadInterestExpense(monthKey, headers);
    } catch {
      setSectionData(SECTION.INTEREST_EXPENSE, INTEREST_EXPENSE_COLUMNS.length, []);
    }

    try {
      await loadOtherExpenses(monthKey, headers);
    } catch {
      setSectionData(SECTION.OTHER_EXPENSES, OTHER_EXPENSES_COLUMNS.length, []);
    }

    // Tracking fees & commission are near-always zero in the source file;
    // start them blank for manual entry.
    setSectionData(SECTION.TRACKING_FEES, TRACKING_FEES_COLUMNS.length, []);
    setSectionData(SECTION.COMMISSION, COMMISSION_COLUMNS.length, []);

    setGridLoading(false);
    if (loanCount) {
      message.success(
        `Loaded ${loanCount} loan${loanCount === 1 ? "" : "s"} for ${sheetNameForMonth(m)}`,
      );
    } else {
      message.info(
        `No existing loans for ${sheetNameForMonth(m)} — starting with a blank sheet`,
      );
    }
  };

  const handleGridMonthChange = (m) => {
    if (!m) return;
    setGridMonth(m);
    loadMonthIntoGrid(m);
  };

  // Insert a row into whichever section tab is currently open.
  const addGridRow = () => {
    const inst = gridInstanceRef.current;
    if (!inst || !inst.length) return;
    const active =
      typeof inst[0]?.parent?.getWorksheetActive === "function"
        ? inst[0].parent.getWorksheetActive()
        : typeof inst[0]?.getWorksheetActive === "function"
          ? inst[0].getWorksheetActive()
        : 0;
    const target = inst[active] || inst[0];
    if (target && typeof target.insertRow === "function") {
      target.insertRow();
      setGridVersion((v) => v + 1);
    }
  };

  const getWorksheetData = (idx) => {
    const inst = gridInstanceRef.current;
    if (!inst || !inst[idx]) return [];
    return inst[idx].getData();
  };

  const readSections = () => {
    const loans = getWorksheetData(SECTION.LOANS).filter(
      (r) => String(r[0] || "").trim() !== "",
    );
    const interestExpense = getWorksheetData(SECTION.INTEREST_EXPENSE).filter(
      (r) => String(r[0] || "").trim() !== "" || num(r[2]) !== 0,
    );
    const trackingFees = getWorksheetData(SECTION.TRACKING_FEES).filter(
      (r) => String(r[0] || "").trim() !== "" || num(r[2]) !== 0,
    );
    const commission = getWorksheetData(SECTION.COMMISSION).filter(
      (r) => String(r[0] || "").trim() !== "" || num(r[2]) !== 0,
    );
    const otherExpenses = getWorksheetData(SECTION.OTHER_EXPENSES).filter(
      (r) => String(r[1] || "").trim() !== "" || num(r[2]) !== 0,
    );
    return {
      loans,
      interestExpense,
      trackingFees,
      commission,
      otherExpenses,
    };
  };

  const computeSummary = (sections) => {
    const loansAmount = sections.loans.reduce((sum, row) => sum + num(row[1]), 0);
    const interestIncome = sections.loans.reduce(
      (sum, row) => sum + num(row[5]),
      0,
    );
    const interestIncomeReceived = sections.loans.reduce((sum, row) => {
      const status = String(row[6] || "").trim().toLowerCase();
      return status === "paid" ? sum + num(row[5]) : sum;
    }, 0);
    const interestExpense = sections.interestExpense.reduce(
      (sum, row) => sum + num(row[5]),
      0,
    );
    const trackingFee = sections.trackingFees.reduce(
      (sum, row) => sum + num(row[2]),
      0,
    );
    const commission = sections.commission.reduce(
      (sum, row) => sum + num(row[2]),
      0,
    );
    const otherExpenses = sections.otherExpenses.reduce(
      (sum, row) => sum + num(row[2]),
      0,
    );
    const netInterestIncome = interestIncome - interestExpense;
    const netInterestAndTracking = netInterestIncome + trackingFee;
    const netOperatingIncome = netInterestAndTracking - commission;
    const profitBeforeTax = netOperatingIncome - otherExpenses;

    return {
      loansAmount,
      interestIncome,
      interestIncomeReceived,
      interestExpense,
      trackingFee,
      commission,
      otherExpenses,
      netInterestIncome,
      netInterestAndTracking,
      netOperatingIncome,
      profitBeforeTax,
    };
  };

  const buildSummaryBlock = (title, line1, line2, line3, value1, value2, value3) => [
    [title],
    [line1, "", "", "", "", value1],
    [line2, "", "", "", "", value2],
    [line3, "", "", "", "", value3],
  ];

  const assembleWorkbook = () => {
    const sections = readSections();
    if (!sections.loans.length) {
      message.warning("Add at least one loan row with a name before validating.");
      return null;
    }

    const summaryValues = computeSummary(sections);
    const aoa = [
      MONTH_COLUMNS,
      ...sections.loans,
      [
        MARKER.TOTAL,
        summaryValues.loansAmount,
        "",
        "",
        "",
        summaryValues.interestIncome,
      ],
      [MARKER.INTEREST_INCOME, "", "", "", "", summaryValues.interestIncome],
      [
        MARKER.INTEREST_INCOME_RECEIVED,
        "",
        "",
        "",
        "",
        summaryValues.interestIncomeReceived,
      ],
      [],
      [],
      [MARKER.INTEREST_EXPENSE],
      INTEREST_EXPENSE_COLUMNS,
      ...sections.interestExpense,
      [MARKER.TOTAL, "", "", "", "", summaryValues.interestExpense],
      [],
      [],
      [MARKER.TRACKING_FEES],
      TRACKING_FEES_COLUMNS,
      ...sections.trackingFees,
      [MARKER.TOTAL, "", summaryValues.trackingFee],
      [],
      [],
      [MARKER.COMMISSION],
      COMMISSION_COLUMNS,
      ...sections.commission,
      [MARKER.TOTAL, "", summaryValues.commission],
      [],
      [],
      [MARKER.OTHER_EXPENSES],
      OTHER_EXPENSES_COLUMNS,
      ...sections.otherExpenses,
      [MARKER.TOTAL, "", summaryValues.otherExpenses],
      [],
      [],
      ...buildSummaryBlock(
        MARKER.NET_INTEREST_INCOME,
        "Interest Income",
        "Interest Expense",
        "Net Interest Income",
        summaryValues.interestIncome,
        summaryValues.interestExpense,
        summaryValues.netInterestIncome,
      ),
      [],
      [],
      ...buildSummaryBlock(
        MARKER.NET_INTEREST_AND_TRACKING,
        "Net Interest Income",
        "Tracking Fee",
        "Net Interest Income and Tracking Fee",
        summaryValues.netInterestIncome,
        summaryValues.trackingFee,
        summaryValues.netInterestAndTracking,
      ),
      [],
      [],
      ...buildSummaryBlock(
        MARKER.NET_OPERATING_INCOME,
        "Net Interest Income and Tracking Fee",
        "Commission",
        "Net Operating Income",
        summaryValues.netInterestAndTracking,
        summaryValues.commission,
        summaryValues.netOperatingIncome,
      ),
      [],
      [],
      ...buildSummaryBlock(
        MARKER.PROFIT_BEFORE_TAX,
        "Net Operating Income",
        "Other Expenses",
        "Profit Before Tax",
        summaryValues.netOperatingIncome,
        summaryValues.otherExpenses,
        summaryValues.profitBeforeTax,
      ),
    ];

    return { aoa, summaryValues };
  };

  // Serialize the grid to an .xlsx File and run it through the existing ETL
  // validation path — identical to uploading a file, so the review/import
  // screens below work unchanged.
  const handleValidateGrid = async () => {
    const workbook = assembleWorkbook();
    if (!workbook) return;

    const sheetName = sheetNameForMonth(gridMonth);
    const ws = XLSX.utils.aoa_to_sheet(workbook.aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    const buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    const generatedFile = new File(
      [buf],
      `jutem-${gridMonth.format("YYYY-MM")}.xlsx`,
      { type: XLSX_MIME },
    );

    setFile(generatedFile);
    setSheet(sheetName);
    setSummary(null);
    setLog("");
    setErrorMessage("");
    setPhase("validating");

    try {
      const parsed = await runEtl(generatedFile, sheetName, false);
      setSummary(parsed);
      setPhase("reviewing");
    } catch (err) {
      fail(err.message);
    }
  };

  const etlToken = () =>
    process.env.REACT_APP_ETL_API_TOKEN ||
    localStorage.getItem("etl_api_token") ||
    "8a2868c39b82bbe2bb3cdda9f7094c6b1f461674cb16775e17aaf07c0d985427";

  const reset = () => {
    setPhase("idle");
    setFile(null);
    setSheet(null);
    setSummary(null);
    setLog("");
    setErrorMessage("");
  };

  const fail = (msg) => {
    setErrorMessage(msg);
    setPhase("error");
  };

  const runEtl = async (chosenFile, chosenSheet, executeMode) => {
    const token = etlToken();
    if (!token) {
      fail(
        "Upload service is not configured on this device. Please contact your administrator.",
      );
      return null;
    }

    const fd = new FormData();
    fd.append("file", chosenFile);
    fd.append("sheet", chosenSheet);
    fd.append("step", "all");
    fd.append("execute", executeMode ? "true" : "false");

    let collected = "";
    try {
      const res = await fetch(`${ETL_API_URL}/monthly-update`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        collected += decoder.decode(value, { stream: true });
        setLog(collected);
      }
    } catch (err) {
      setLog(collected + `\n[ERROR] ${err.message}\n`);
      throw err;
    }

    const parsed = extractSummary(collected);
    if (!parsed) {
      throw new Error(
        "We couldn't read the summary from the upload service. See technical details below.",
      );
    }
    return parsed;
  };

  const handleFileSelect = async (f) => {
    setFile(f);
    setSheet(null);
    setSummary(null);
    setLog("");
    setErrorMessage("");
    setPhase("validating");

    const token = etlToken();
    if (!token) {
      fail(
        "Upload service is not configured on this device. Please contact your administrator.",
      );
      return false;
    }

    let chosenSheet = null;
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await fetch(`${ETL_API_URL}/sheets`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      chosenSheet = pickLatestMonthSheet(data.sheets);
      if (!chosenSheet) {
        fail(
          "We couldn't find a month tab in this file. Make sure your sheet is named like 'APRIL 2026'.",
        );
        return false;
      }
      setSheet(chosenSheet);
    } catch (err) {
      fail(`We couldn't read this file: ${err.message}`);
      return false;
    }

    try {
      const parsed = await runEtl(f, chosenSheet, false);
      setSummary(parsed);
      setPhase("reviewing");
    } catch (err) {
      fail(err.message);
    }

    return false; // prevent antd auto-upload
  };

  const handleImport = async () => {
    if (!file || !sheet) return;
    setPhase("importing");
    setLog("");
    try {
      const parsed = await runEtl(file, sheet, true);
      setSummary(parsed);
      setPhase("done");
    } catch (err) {
      fail(err.message);
    }
  };

  const technicalDetails = log ? (
    <Collapse
      style={{ marginTop: 24 }}
      items={[
        {
          key: "log",
          label: "Show technical details",
          children: (
            <pre
              style={{
                whiteSpace: "pre-wrap",
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                fontSize: 12,
                maxHeight: 400,
                overflow: "auto",
                margin: 0,
                padding: 12,
                background: "#0f172a",
                color: "#e2e8f0",
                borderRadius: 6,
              }}
            >
              {log}
            </pre>
          ),
        },
      ]}
    />
  ) : null;

  const renderUploadTab = () => (
    <Dragger
      accept=".xlsx"
      maxCount={1}
      beforeUpload={handleFileSelect}
      showUploadList={false}
    >
      <p className="ant-upload-drag-icon">
        <InboxOutlined />
      </p>
      <p className="ant-upload-text" style={{ fontSize: 18 }}>
        Drop your Jutem monthly Excel file here
      </p>
      <p className="ant-upload-hint">
        Or click to browse. We'll check the file before importing anything.
      </p>
    </Dragger>
  );

  const renderGridTab = () => (
    <div>
      <Space wrap style={{ marginBottom: 16 }}>
        <Text strong>Month:</Text>
        <DatePicker
          picker="month"
          value={gridMonth}
          onChange={handleGridMonthChange}
          allowClear={false}
        />
        <Button icon={<PlusOutlined />} onClick={addGridRow}>
          Add row
        </Button>
        <Button
          type="default"
          icon={<ReloadOutlined />}
          onClick={() => loadMonthIntoGrid(gridMonth)}
        >
          Reload from system
        </Button>
      </Space>

      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        Edit cells directly, like a spreadsheet. Rows are pre-filled from{" "}
        <strong>{sheetNameForMonth(gridMonth)}</strong> when available. Nothing
        is saved until you validate and import.
      </Text>

      <Spin spinning={gridLoading}>
        <div
          ref={gridContainerRef}
          style={{ overflowX: "auto", marginBottom: 16 }}
        />
      </Spin>

      {(() => {
        const sections = readSections();
        const summaryValues = computeSummary(sections);
        return (
          <Card
            size="small"
            title="Summary preview"
            style={{ marginBottom: 16 }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Text strong>Net Interest Income</Text>
                <div style={{ marginTop: 8 }}>
                  <div>Interest Income: {fmtK(summaryValues.interestIncome)}</div>
                  <div>Interest Expense: {fmtK(summaryValues.interestExpense)}</div>
                  <div>Net: {fmtK(summaryValues.netInterestIncome)}</div>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <Text strong>Net Interest Income and Tracking Fee</Text>
                <div style={{ marginTop: 8 }}>
                  <div>Net: {fmtK(summaryValues.netInterestIncome)}</div>
                  <div>Tracking Fee: {fmtK(summaryValues.trackingFee)}</div>
                  <div>Combined: {fmtK(summaryValues.netInterestAndTracking)}</div>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <Text strong>Net Operating Income</Text>
                <div style={{ marginTop: 8 }}>
                  <div>Combined: {fmtK(summaryValues.netInterestAndTracking)}</div>
                  <div>Commission: {fmtK(summaryValues.commission)}</div>
                  <div>Operating: {fmtK(summaryValues.netOperatingIncome)}</div>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <Text strong>Profit Before Tax</Text>
                <div style={{ marginTop: 8 }}>
                  <div>Operating: {fmtK(summaryValues.netOperatingIncome)}</div>
                  <div>Other Expenses: {fmtK(summaryValues.otherExpenses)}</div>
                  <div>Profit Before Tax: {fmtK(summaryValues.profitBeforeTax)}</div>
                </div>
              </Col>
            </Row>
          </Card>
        );
      })()}

      <div style={{ textAlign: "right" }}>
        <Button
          type="primary"
          size="large"
          icon={<CloudUploadOutlined />}
          onClick={handleValidateGrid}
        >
          Validate &amp; review
        </Button>
      </div>
    </div>
  );

  const renderIdle = () => (
    <Card>
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane
          tab={
            <span>
              <InboxOutlined /> Upload file
            </span>
          }
          key="upload"
        >
          {renderUploadTab()}
        </TabPane>
        <TabPane
          tab={
            <span>
              <TableOutlined /> Enter / edit in grid
            </span>
          }
          key="grid"
          forceRender
        >
          {renderGridTab()}
        </TabPane>
      </Tabs>
    </Card>
  );

  const renderValidating = () => (
    <Card>
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <Spin size="large" />
        <Title level={4} style={{ marginTop: 24 }}>
          Checking your file…
        </Title>
        <Text type="secondary">
          We're reading <strong>{file?.name}</strong> and validating the data.
          Nothing has been imported yet.
        </Text>
      </div>
    </Card>
  );

  const renderReviewing = () => {
    const parsed = summary?.parsed || {};
    const totals = summary?.totals || {};
    const actions = summary?.actions || {};
    const errs = summary?.errors || [];

    const newPaymentsToRecord =
      (actions.payments_created || 0) + (actions.status_to_paid || 0);

    return (
      <>
        <Card>
          <div style={{ marginBottom: 16 }}>
            <Title level={4} style={{ margin: 0 }}>
              {summary?.sheet} is ready to import
            </Title>
            <Text type="secondary">
              Review the numbers below. Nothing will be saved until you click{" "}
              <strong>Import to system</strong>.
            </Text>
          </div>

          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Duplicate-safe import"
            description="Existing borrowers are reused, matching monthly loan rows are updated instead of re-created, and already-recorded month expenses are skipped."
          />

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Card size="small">
                <Statistic
                  title="New loans"
                  value={actions.new_loans || 0}
                  suffix={
                    parsed.loans_total
                      ? `of ${parsed.loans_total} on the sheet`
                      : ""
                  }
                />
                <Text type="secondary">
                  Total: {formatMoney(totals.loans_amount)}
                </Text>
              </Card>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Card size="small">
                <Statistic
                  title="Payments to record"
                  value={newPaymentsToRecord}
                />
                <Text type="secondary">
                  {actions.status_to_paid || 0} previously pending, now paid
                </Text>
              </Card>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Card size="small">
                <Statistic
                  title="New borrowers"
                  value={actions.new_borrowers || 0}
                />
                <Text type="secondary">Created if not already in the system</Text>
              </Card>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Card size="small">
                <Statistic
                  title="Interest expense payments"
                  value={actions.interest_expenses_created || 0}
                />
                <Text type="secondary">
                  Total: {formatMoney(totals.interest_expense)}
                </Text>
              </Card>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Card size="small">
                <Statistic
                  title="Other expenses"
                  value={actions.expenses_created || 0}
                />
                <Text type="secondary">
                  Total: {formatMoney(totals.other_expenses)}
                </Text>
              </Card>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Card size="small">
                <Statistic
                  title="Interest income"
                  value={formatMoney(totals.interest_income)}
                  valueStyle={{ fontSize: 18 }}
                />
                <Text type="secondary">From this month's loans</Text>
              </Card>
            </Col>
          </Row>

          {errs.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
              message={`${errs.length} warning${errs.length === 1 ? "" : "s"} during validation`}
              description={
                <ul style={{ marginBottom: 0 }}>
                  {errs.slice(0, 5).map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                  {errs.length > 5 && (
                    <li>…and {errs.length - 5} more (see technical details)</li>
                  )}
                </ul>
              }
            />
          )}

          <div style={{ marginTop: 24, textAlign: "right" }}>
            <Button onClick={reset} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<CloudUploadOutlined />}
              onClick={handleImport}
            >
              Import to system
            </Button>
          </div>
        </Card>
        {technicalDetails}
      </>
    );
  };

  const renderImporting = () => (
    <Card>
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <Spin size="large" />
        <Title level={4} style={{ marginTop: 24 }}>
          Importing {summary?.sheet}…
        </Title>
        <Text type="secondary">
          This can take a couple of minutes. Please don't close this page.
        </Text>
      </div>
    </Card>
  );

  const renderDone = () => {
    const actions = summary?.actions || {};
    const newPayments =
      (actions.payments_created || 0) + (actions.status_to_paid || 0);
    const errs = summary?.errors || [];

    return (
      <>
        <Card>
          <Result
            status="success"
            icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}
            title={`${summary?.sheet} imported successfully`}
            subTitle={
              <span>
                Added {actions.new_loans || 0} loans,{" "}
                {newPayments} payments,{" "}
                {actions.interest_expenses_created || 0} interest expense
                payments, and {actions.expenses_created || 0} other expenses.
              </span>
            }
            extra={[
              <Button
                type="primary"
                key="loans"
                onClick={() => navigate("/amortizations")}
              >
                View Jutem loans
              </Button>,
              <Button key="another" icon={<ReloadOutlined />} onClick={reset}>
                Upload another month
              </Button>,
            ]}
          />
          {errs.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={`${errs.length} warning${errs.length === 1 ? "" : "s"} during import`}
              description="The import completed but some rows had issues. Open technical details below for the full list."
            />
          )}
        </Card>
        {technicalDetails}
      </>
    );
  };

  const renderError = () => (
    <>
      <Card>
        <Result
          status="error"
          title="Something went wrong"
          subTitle={errorMessage}
          extra={[
            <Button
              type="primary"
              key="retry"
              icon={<ReloadOutlined />}
              onClick={reset}
            >
              Try again
            </Button>,
          ]}
        />
      </Card>
      {technicalDetails}
    </>
  );

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}>
      <Title level={3}>Jutem Fund — Monthly Upload</Title>
      <Paragraph type="secondary">
        Upload the full monthly Excel file once to import borrowers, loans,
        payments, interest expenses, tracking fees, and other expenses in one
        pass.
      </Paragraph>
      <Paragraph type="secondary" style={{ marginTop: -8 }}>
        The import is duplicate-safe: existing borrowers are matched, existing
        month rows are updated where appropriate, and already-recorded expenses
        are skipped.
      </Paragraph>

      {phase === "idle" && renderIdle()}
      {phase === "validating" && renderValidating()}
      {phase === "reviewing" && renderReviewing()}
      {phase === "importing" && renderImporting()}
      {phase === "done" && renderDone()}
      {phase === "error" && renderError()}
    </div>
  );
};

export default JutemEtlUploadPage;
